/* eslint-disable max-len */
import AgoraRTC, {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-react';
import { useContext, useEffect, useRef, useState } from 'react';

import { LinkIcon, VideoIcon } from 'assets/icons';
import { AllPaymentProcess } from 'common/all-payment-process';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { BuyClipsProcess } from 'common/buy-clips-process';
import { BuyExtraCallTimeProcess } from 'common/buy-extra-call-time-process';
import { CopyLink } from 'common/copy-link';
import { NotificationType, PaymentType, RecordingStatus } from 'common/enums';
import { ModalBuyTimeSubscription } from 'common/modal-clips-time-subscription';
import { ModalWarning } from 'common/modal-warning';
import { Notification } from 'common/notification';
import { Select } from 'common/select';
import { SensorToggleButton } from 'common/sensor-toggle-button';
import {
  agoraOptions,
  apiBaseURL,
  msMaxClipChunkDuration,
  msMaxRecordingPeriodTime,
  msToHideClipRecordedWindow,
  msToRecallPingToGetVideoCallUsedTime,
  questionsMaxLength,
  sAnnualWarningTimeVideoCall,
} from 'config/constants';
import { AppContext, SensorSelectionContext, appActions } from 'context';
import { SensorSelectionActions } from 'context/sensor-selection-context/action-types';
import { isPromptContent } from 'helpers/memories';
import { refreshPage } from 'helpers/refresh-page';
import {
  canUpgradeVault,
  capitalizeFirstLetter,
  classnames,
  getPermissionsByVault,
  hasVaultAccess,
  isFreeTrial,
  isOwner,
  isTemporaryUser,
  sleep,
  userPermissionUpgrade,
} from 'helpers/utils';
import {
  getOtherRole,
  getUserRole,
  isRoleChanged,
  userHasRole,
  videocallIsFull,
} from 'helpers/videocall-helper';
import { useLoadSensorSelection } from 'hooks/use-load-sensor-selection';
import {
  VideoCallRole,
  useVideocallWebsocket,
} from 'hooks/use-videocall-websocket';
import { cloneDeep } from 'lodash';
import { Clip } from 'models/clip';
import { Album } from 'models/album';
import { NotificationObject } from 'models/notificationObject';
import { AgoraController } from 'networking/controllers/agora-controller';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { useHistory } from 'react-router-dom';
import { RouteName, goToPage } from 'routes';
import { ClipRecordedWindow } from './components/clip-recorded-window';
import { DeleteConfirmationWindow } from './components/delete-confirmation-window';
import { ModalConnectionFailed } from './components/modal-connection-failed';
import { ModalOtherUserAudioFailed } from './components/modal-other-user-audio-failed';
import { ModalRoleChanging } from './components/modal-role-changing';
import { OwnerMenu } from './components/owner-menu';
import { Video } from './components/video';
import { WarningReachingClipsLimit } from './components/warning-reaching-clips-limit';
import { WarningReachingTimeLimit } from './components/warning-reaching-time-limit';
import { WarningRoleUpdated } from './components/warning-role-updated';
import { WarningUserLeftCall } from './components/warning-user-left-call';
import styles from './video-call.module.scss';

export type PoolContainerType = {
  clipMain: ClipType | null;
  clipsExtras: ClipType[];
};

// What recording a topic fills: a planner prompt (its clip, answered in
// place in its album) or an album with nothing in it yet.
type TopicTarget = { clipId?: number; albumId?: number };

type BackUpQuestionSelectedType = {
  index: number;
  backUpQuestion: ClipType | null;
};

type VideoCallProps = {
  id: string;
  vault: VaultType;
  user: UserType;
  subSchemes: SubscriptionTypeType[];
  notifyVaultChange: (vault: VaultType) => void;
  notifyEndCall: (limitReached?: boolean) => Promise<void>;
  refetchVault: () => Promise<void>;
  initialRole: VideoCallRole;
  // Started from an album's planner: only that album's prompts are asked,
  // and a custom recording is saved in it rather than in a new album.
  albumId?: number;
  notifyEndCallGuest: () => void;
};

const VideoCall: React.FC<VideoCallProps> = ({
  vault,
  user,
  subSchemes,
  notifyVaultChange,
  notifyEndCall,
  refetchVault,
  initialRole,
  albumId: scopedAlbumId,
  notifyEndCallGuest,
}) => {
  const history = useHistory();

  const { dispatch } = useContext(AppContext);

  const [published, setPublished] = useState<boolean>(false);
  const wasInCallRef = useRef<boolean>(false);
  const [token, setToken] = useState('');

  const [otherUserInCall, setOtherUserInCall] = useState<boolean>(false);
  const [otherUserTracks, setOtherUserTracks] =
    useState<IAgoraRTCRemoteUser | null>(null);
  const [otherUserTrackState, setOtherUserTrackState] = useState({
    audio: false,
    video: false,
  });
  const [otherUserAudioFailed, setOtherUserAudioFailed] = useState(false);
  const [showOtherUserAudioFailed, setShowOtherUserAudioFailed] =
    useState(false);

  const [myTracks, setMyTracks] =
    useState<[IMicrophoneAudioTrack, ICameraVideoTrack | undefined]>();

  const [newAnsweredQuestions, setNewAnsweredQuestions] = useState<ClipType[]>(
    [],
  );
  const [questionSelected, setQuestionSelected] = useState<ClipType>(
    new Clip(),
  );
  const [pendingQuestionsList, setPendingQuestionsList] = useState<ClipType[]>(
    [],
  );
  // Keyed by the id of the topic's row in the pending list.
  const topicTargets = useRef(new Map<number, TopicTarget>());

  const [showModalBuyClips, setShowModalBuyClips] = useState<boolean>(false);

  const [startPaymentProcess, setStartPaymentProcess] = useState(false);

  const [endCallWarning, setEndCallWarning] = useState<boolean>(false);

  const [showModalBuyClipsOrTime, setShowModalBuyClipsOrTime] =
    useState<boolean>(false);

  const [showModalBuyTime, setShowModalBuyExtraTime] = useState<boolean>(false);

  const [showClipRecordedWindow, setShowClipRecordedWindow] =
    useState<boolean>(false);
  const [displayClipRecordedWindow, setDisplayClipRecordedWindow] =
    useState<boolean>(false);
  const [allClipsCount, setAllClipsCount] = useState<number>(0);
  const [clipsCountRecorded, setClipsCountRecorded] = useState<number>(1);

  const [forceStopRecording, setForceStopRecording] = useState<boolean>(false);

  const [showWarningReachingClipLimit, setShowWarningReachingClipLimit] =
    useState<boolean>(false);
  const [userLeftCall, setUserLeftCall] = useState<VideoCallRole | undefined>(
    undefined,
  );

  const [showWarningReachingTimeLimit, setShowWarningReachingTimeLimit] =
    useState<boolean>(false);
  const [sCallTimeLeft, setSCallTimeLeft] = useState<number>(
    vault.videoCallProps?.maxCallTime && vault.videoCallProps.usedCallTime
      ? vault.videoCallProps.maxCallTime - vault.videoCallProps.usedCallTime
      : 1000,
  );

  const [showDeleteConfirmationWindow, setShowDeleteConfirmationWindow] =
    useState<boolean>(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [showRoleSwitched, setShowRoleSwitched] = useState<boolean>(false);

  const [showModalConnectionFailed, setShowModalConnectionFailed] =
    useState<boolean>(false);

  const [changeRoleLoading, setChangeRoleLoading] = useState<boolean>(false);

  const [signUpModal, setShowModalSignUp] = useState<boolean>(false);

  const channelName = useRef<string>('');

  const role = useRef<VideoCallRole | undefined>(undefined);

  const initialRoleSet = useRef<boolean>(false);

  const waitForRequest = useRef<boolean>(true);

  const sid = useRef<string>('');
  const resourceId = useRef<string>('');

  const [vaultPermissions, setVaultPermissions] = useState<
    SubscriptionPermissionsType | undefined
  >();

  const interviewerIsRecording = useRef<boolean>(false);
  const stoppingRecording = useRef<boolean>(false);

  const poolContainer = useRef<PoolContainerType>({
    clipMain: null,
    clipsExtras: [],
  });
  const videoPartNumber = useRef<number>(2);
  const backupQuestionSelected = useRef<BackUpQuestionSelectedType>({
    index: -1,
    backUpQuestion: null,
  });
  const maxNumberRecordingChunks = useRef<number>(
    msMaxRecordingPeriodTime / msMaxClipChunkDuration,
  );

  // Secure constant token associated with vault
  const videoCallToken = vault.videoCallProps?.videoCallToken!;
  const {
    online,
    sendRecordingStarted,
    sendRecordingEnded,
    recordingInProgress,
    updateRole,
    callInformation,
    socketDisconnected,
    getCallInformation,
    questionRecording,
  } = useVideocallWebsocket(
    vault?.id || null,
    videoCallToken,
    questionSelected.name,
  );

  const respondentIsGuest = useRef(
    !!callInformation?.videocallRespondentIsGuest,
  );

  const userIdForVideocall = hasVaultAccess(user, vault) ? user.id : undefined;

  // Get the call information
  useEffect(() => {
    if (online && !callInformation) {
      getCallInformation();
    }
  }, [online, callInformation]);

  // Call information arrives and need to react to a role changed
  useEffect(() => {
    if (
      waitForRequest.current &&
      callInformation &&
      role.current &&
      isRoleChanged(role.current, callInformation, userIdForVideocall)
    ) {
      setChangeRoleLoading(true);
      // Wait 1 second before update role
      setTimeout(() => {
        waitForRequest.current = false;
        // I need to ask for call information again to be able to run the code
        // that's responsible to update the role, which is in the useEffect below
        getCallInformation();
      }, 2000);
    }
  }, [callInformation]);

  useEffect(() => {
    if (isTemporaryUser(user) && vault.clipAmount === vault.maxClips) {
      setShowModalSignUp(true);
    }
  }, []);

  // Update the role after waiting for make the request
  useEffect(() => {
    if (
      !waitForRequest.current &&
      callInformation &&
      role.current &&
      isRoleChanged(role.current, callInformation, userIdForVideocall)
    ) {
      const newRole = getOtherRole(role.current!);
      role.current = newRole;
      updateRole(newRole);
      setChangeRoleLoading(false);
      setShowRoleSwitched(true);
      if (newRole === VideoCallRole.INTERVIEWER) {
        refetchVault();
      }
      waitForRequest.current = true;
    }
  }, [callInformation]);

  // Check if is able to entering the call and set the initial role
  useEffect(() => {
    if (
      callInformation &&
      !initialRoleSet.current &&
      !userHasRole(callInformation, userIdForVideocall)
    ) {
      if (videocallIsFull(callInformation)) {
        notifyEndCall();
      } else {
        updateRole(initialRole);
        initialRoleSet.current = true;
      }
    }
  }, [callInformation]);

  // Save initial role after setting it
  useEffect(() => {
    if (
      callInformation &&
      !role.current &&
      userHasRole(callInformation, userIdForVideocall)
    ) {
      role.current = getUserRole(callInformation, user.id);
    }
  }, [callInformation]);

  // If the web socket connection fails
  useEffect(() => {
    if (socketDisconnected) {
      setShowModalConnectionFailed(true);
    }
  }, [socketDisconnected]);

  useEffect(() => {
    respondentIsGuest.current = !!callInformation?.videocallRespondentIsGuest;
  }, [callInformation]);

  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const joinStartedRef = useRef<boolean>(false);

  if (!clientRef.current) {
    clientRef.current = AgoraRTC.createClient({
      mode: 'rtc',
      codec: 'vp8',
    });
  }

  const client = clientRef.current;

  const { sensorSelection, updateSensorSelection } = useContext(
    SensorSelectionContext,
  );
  useLoadSensorSelection();

  const createAgoraTracks = async () => {
    let myAudio: IMicrophoneAudioTrack;
    let myVideo: ICameraVideoTrack | undefined;

    try {
      // "{ encoderConfig: 'music_standard' } by default.
      myAudio = await AgoraRTC.createMicrophoneAudioTrack({
        microphoneId: sensorSelection.microphone.selectedOption?.deviceId,
      });
    } catch (e) {
      notifyEndCall();
      return;
    }

    try {
      //  640 x 480 by default
      myVideo = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: '1080p',
        cameraId: sensorSelection.camera.selectedOption?.deviceId,
      });
    } catch (e) {
      updateSensorSelection({
        type: SensorSelectionActions.CAMERA_TOGGLED,
        enabled: false,
      });
      myVideo = undefined;
    }
    setMyTracks([myAudio, myVideo]);
    wasInCallRef.current = true;
  };

  const leaveChannel = async (limitReached?: boolean) => {
    if (myTracks) {
      if (myTracks[0]) {
        myTracks[0].close();
      }

      if (myTracks[1]) {
        myTracks[1].close();
      }
    }
    setMyTracks(undefined);
    if (client) {
      await client.leave();
      client.removeAllListeners();
    }
    joinStartedRef.current = false;

    setPublished(false);
    await notifyEndCall(!!limitReached);
  };

  useEffect(() => {
    const unlisten = history.listen((_, action) => {
      if (action === 'POP') {
        leaveChannel();
      }
    });

    return () => {
      unlisten();
    };
  }, [history]);

  // Cleanup when component unmounts
  useEffect(
    () => () => {
      if (wasInCallRef.current) {
        leaveChannel();
      }
    },
    [],
  );

  const albumToClipType = (album: Album): ClipType =>
    Object.assign(new Clip(), {
      id: album.id,
      name: album.name,
      recordingStatus: RecordingStatus.noVideo,
    });

  const getCustomRecordingClip = (alb: VaultType): ClipType => {
    const customItem = new Clip();
    customItem.recordingStatus = RecordingStatus.noVideo;
    customItem.name = 'Custom recording';
    const totalQuestions = alb.unansweredAlbums?.length || 0;
    if (totalQuestions >= alb.maxClips) {
      customItem.name = 'Custom recording (Reached limit)';
    }
    return customItem;
  };

  // The interviewer's topics: every planner prompt still to be answered, in
  // its album's order, then the albums that have nothing in them yet. Only
  // the one album's when the interview was started from it.
  const buildPendingQuestionsList = (alb: VaultType): ClipType[] => {
    const customItem = getCustomRecordingClip(alb);
    const targets = new Map<number, TopicTarget>();
    const topics: ClipType[] = [];
    const inScope = (album: { id: number }) =>
      scopedAlbumId == null || album.id === scopedAlbumId;

    (alb.allAlbums || []).filter(inScope).forEach((album) => {
      const prompts: AlbumContentSerialized[] = (album.contents ?? []).filter(
        isPromptContent,
      );
      prompts.forEach((prompt) => {
        const clipId = prompt.content?.id;
        if (clipId == null || targets.has(clipId)) return;
        targets.set(clipId, { clipId, albumId: album.id });
        topics.push(
          Object.assign(new Clip(), {
            id: clipId,
            name: prompt.name || prompt.content?.name || 'Untitled',
            recordingStatus: RecordingStatus.noVideo,
          }),
        );
      });
    });

    (alb.unansweredAlbums || []).filter(inScope).forEach((album) => {
      // An album that only holds prompts is already listed through them.
      const hasPrompts = (album.contents ?? []).some(isPromptContent);
      if (hasPrompts || targets.has(album.id)) return;
      targets.set(album.id, { albumId: album.id });
      topics.push(albumToClipType(album));
    });

    topicTargets.current = targets;
    return [customItem, ...topics];
  };

  const calculateCallTimeLeft = ({
    usedCallTime,
    maxCallTime,
  }: AgoraVideoCallPropsType) => {
    const secondTimeLeft = maxCallTime! - +usedCallTime!;

    setSCallTimeLeft(secondTimeLeft);
  };

  const setInterviewerData = async (vaultResponse: VaultType) => {
    try {
      const permissionsObject = getPermissionsByVault(
        subSchemes,
        vaultResponse,
      );
      if (!vaultPermissions && permissionsObject) {
        setVaultPermissions(permissionsObject);
      }

      if (!allClipsCount) {
        setAllClipsCount(vaultResponse.clipAmount);
      }

      const pendingList = buildPendingQuestionsList(vaultResponse);
      setPendingQuestionsList(pendingList);
      setQuestionSelected(
        pendingList.length === 1 ? pendingList[0] : pendingList[1],
      );

      if (!sCallTimeLeft) {
        calculateCallTimeLeft(vaultResponse.videoCallProps!);
      }
    } catch (err) {
      leaveChannel();
    }
  };

  // Update microphone and camera devices
  useEffect(() => {
    if (myTracks && published) {
      if (myTracks[0] && sensorSelection.microphone.selectedOption?.deviceId) {
        myTracks[0].setDevice(
          sensorSelection.microphone.selectedOption?.deviceId,
        );
      }
      if (myTracks[1] && sensorSelection.camera.selectedOption?.deviceId) {
        myTracks[1].setDevice(sensorSelection.camera.selectedOption?.deviceId);
      }
    }
  }, [
    myTracks,
    published,
    sensorSelection.camera.selectedOption?.deviceId,
    sensorSelection.microphone.selectedOption?.deviceId,
  ]);

  // Enable/Disable microphone and camera
  useEffect(() => {
    // Don't disable anything if the tracks aren't published yet
    // Disabled tracks cannot be published
    if (myTracks && published) {
      if (myTracks[0]) {
        myTracks[0].setEnabled(sensorSelection.microphone.enabled);
      }
      if (myTracks[1]) {
        myTracks[1].setEnabled(sensorSelection.camera.enabled);
      }
    }
  }, [
    myTracks,
    published,
    sensorSelection.camera.enabled,
    sensorSelection.microphone.enabled,
  ]);

  const getVaultAndSetData = async (userRole: VideoCallRole) => {
    if (userRole === VideoCallRole.INTERVIEWER) {
      setInterviewerData(vault);
    }
  };

  // Once per call: in development React runs this twice (Strict Mode), which
  // opened the camera and mic twice and left one pair running.
  const tracksRequestedRef = useRef(false);
  useEffect(() => {
    if (tracksRequestedRef.current) return;
    tracksRequestedRef.current = true;
    createAgoraTracks();
  }, []);

  useEffect(() => {
    getVaultAndSetData(role.current ?? initialRole);
  }, [vault]);

  useEffect(() => {
    if (vault.maxClips > 0) {
      if (allClipsCount >= vault.maxClips - 3) {
        setShowWarningReachingClipLimit(true);
      } else {
        setShowWarningReachingClipLimit(false);
      }
    }
  }, [allClipsCount, vaultPermissions]);

  const updateTimeLeftFromPing = async () => {
    const response = await AgoraController.getNewVideoCallUsedTime(vault!.id);
    const secondTimeLeft =
      (vault.videoCallProps?.maxCallTime || vaultPermissions!.usersCallTime) -
      response.usedCallTime;

    setSCallTimeLeft(secondTimeLeft);
  };

  useEffect(() => {
    let timeLeftPing: NodeJS.Timeout;
    let timeLeftFront: NodeJS.Timeout;

    if (otherUserInCall && role.current === VideoCallRole.INTERVIEWER) {
      timeLeftPing = setInterval(() => {
        updateTimeLeftFromPing();
      }, msToRecallPingToGetVideoCallUsedTime);

      timeLeftFront = setInterval(() => {
        setSCallTimeLeft((prev) => prev - 1);
      }, 1000);
    }

    return () => {
      clearInterval(timeLeftFront);
      clearInterval(timeLeftPing);
    };
  }, [otherUserInCall]);

  const hideSmooth = () => {
    setDisplayClipRecordedWindow(false);
    setTimeout(() => setShowClipRecordedWindow(false), 800);
  };

  const cleanPoolUpdateVideoPartNumber = () => {
    videoPartNumber.current = 2;
    poolContainer.current = {
      clipMain: null,
      clipsExtras: [],
    };
  };

  const hideFast = () => {
    setDisplayClipRecordedWindow(false);
    setShowClipRecordedWindow(false);
    cleanPoolUpdateVideoPartNumber();
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (showClipRecordedWindow) {
      timer = setInterval(() => {
        hideSmooth();
        cleanPoolUpdateVideoPartNumber();
      }, msToHideClipRecordedWindow);
    }

    return () => {
      clearInterval(timer);
    };
  }, [showClipRecordedWindow]);

  const fetchToken = () => AgoraController.getAgoraToken(videoCallToken);

  const handleErrorRecording = (message: string) => {
    setErrorMessage(message);
    setShowError(true);
  };

  useEffect(() => {
    if (!client || !myTracks) return undefined;

    //  ----------------LISTENERS -----------------------------------------
    client.on('user-joined', async (newGuest) => {
      setOtherUserInCall(true);
      setUserLeftCall(undefined);
      setOtherUserTracks(newGuest);
    });

    client.on('user-published', async (newGuest, mediaType) => {
      await client.subscribe(newGuest, mediaType);

      setOtherUserTracks(newGuest);

      if (mediaType === 'video') {
        setOtherUserTrackState((prev) => ({ ...prev, video: true }));
      }
      if (mediaType === 'audio') {
        if (newGuest.audioTrack) {
          newGuest.audioTrack?.play();
          setOtherUserAudioFailed(false);
          setOtherUserTrackState((prev) => ({ ...prev, audio: true }));
        } else {
          setTimeout(() => {
            const remoteAudioTrack = newGuest.audioTrack;
            if (remoteAudioTrack) {
              remoteAudioTrack.play();
              setOtherUserTrackState((prev) => ({ ...prev, audio: true }));
            } else {
              setShowOtherUserAudioFailed(true);
              setOtherUserAudioFailed(true);
            }
          }, 1000); // Retry after 1 second
        }
      }
    });

    client.on('user-unpublished', (type: string) => {
      if (type === 'video') {
        setOtherUserTrackState((prev) => ({ ...prev, video: false }));
      }
      if (type === 'audio') {
        setOtherUserTrackState((prev) => ({ ...prev, audio: false }));
      }
    });

    client.on('user-left', async () => {
      setOtherUserInCall(false);
      setOtherUserTracks(null);
      setOtherUserTrackState({ audio: false, video: false });
      setOtherUserAudioFailed(false);

      if (
        respondentIsGuest.current &&
        role.current !== VideoCallRole.INTERVIEWER
      ) {
        notifyEndCallGuest();
      } else if (role.current) {
        setUserLeftCall(getOtherRole(role.current));
      }

      if (interviewerIsRecording.current) {
        setForceStopRecording(true);
        interviewerIsRecording.current = false;
      }
    });

    client.on('token-privilege-will-expire', async () => {
      const response = await fetchToken();
      await client.renewToken(response.token);
    });

    // When token-privilege-did-expire occurs, fetches a new token from the server and call join to rejoin the channel.
    client.on('token-privilege-did-expire', async () => {
      const response = await fetchToken();
      channelName.current = response.channelName;

      await client.join(
        agoraOptions.appId!,
        response.channelName,
        response.token,
        +response.uid,
      );
    });

    //  -----------END LISTENERS  ----------------------------------

    const joinAndPublish = async () => {
      try {
        const response = await fetchToken();

        channelName.current = response.channelName;
        setToken(response.token);

        await client.join(
          agoraOptions.appId!,
          response.channelName,
          response.token,
          +response.uid,
        );

        if (myTracks?.[1]) {
          // If audio and video are both available, publish both.
          await client.publish([myTracks[0], myTracks[1]]);
        } else {
          // If only audio is available, publish only audio.
          await client.publish(myTracks![0]);
        }
        setPublished(true);
        wasInCallRef.current = true;
      } catch (error: any) {
        handleErrorRecording(error.message);
      }
    };

    // One join per call. The cleanup doesn't reset this: React runs effects
    // twice in development (Strict Mode), and joining again while the first
    // join is still connecting fails ("Client already in
    // connecting/connected state"). Cleared again on leaving the call.
    if (!published && !joinStartedRef.current) {
      joinStartedRef.current = true;
      joinAndPublish();
    }

    return () => {
      client.removeAllListeners();
    };
  }, [myTracks]);

  const handlePossibleDeleteClip = () => {
    setDisplayClipRecordedWindow(false);
    setShowClipRecordedWindow(false);
    setShowDeleteConfirmationWindow(true);
  };

  const handleDeleteAllRecordedClips = async () => {
    try {
      await VaultsController.deleteClip(poolContainer.current.clipMain!.id);
    } catch (err: any) {
      setShowError(true);
      return;
    }

    cleanPoolUpdateVideoPartNumber();

    setShowDeleteConfirmationWindow(false);
  };

  const startRecording = async () => {
    const requestData: StartRecordingRequestType = {
      token,
    };

    const response = await AgoraController.startRecording(
      videoCallToken,
      requestData,
    );

    sid.current = response.sid;
    resourceId.current = response.resourceId;
  };

  const stopRecording = async (
    clipName: string,
    albumId?: number,
    clipId?: number,
  ) => {
    const requestData: StopRecordingRequestType = {
      sid: sid.current,
      resourceId: resourceId.current,
      clipName,
      albumId,
      // A planner prompt: the recording answers it in place.
      clipId,
    };

    const stopResponse = await AgoraController.stopRecording(
      videoCallToken,
      requestData,
    );
    return stopResponse;
  };

  const removeQuestionFromPendingListAndSelectNext = (qSelected: ClipType) => {
    if (qSelected.id === -1) return;
    const list = pendingQuestionsList;
    const index = list.findIndex((q) => q.id === qSelected.id);
    const updated = list.filter((q) => q.id !== qSelected.id);
    setPendingQuestionsList(updated);
    if (updated[index]) {
      setQuestionSelected(updated[index]);
    } else if (updated.length > 2) {
      setQuestionSelected(updated[1]);
    } else {
      setQuestionSelected(updated[0]);
    }
  };

  const handleQuestionsSelected = (question: ClipType) => {
    setQuestionSelected(question);
  };

  // ! Never used because frontend handle only the main clip. "clipsExtras" will be always empty. Backend will be in charge of splitting the video.
  const insertClipRecordedInPoolClipsExtras = (clip: ClipType) => {
    poolContainer.current.clipsExtras.push(clip);
  };

  const insertClipRecordedInPoolClipMain = (clip: ClipType) => {
    poolContainer.current.clipMain = clip;
  };

  const noClipMainInPool = () => !poolContainer.current.clipMain;

  const setBackUpQuestionSelected = () => {
    const index = pendingQuestionsList.findIndex(
      (clip: ClipType) => clip.id === questionSelected.id,
    );

    backupQuestionSelected.current = {
      index,
      backUpQuestion: questionSelected,
    };
  };

  const addAllPoolClipInNewAnsweredList = (
    newAnswered: ClipType[],
    clipMain: ClipType,
    clipsExtras: ClipType[],
  ) => {
    const newAnsweredListCopy = cloneDeep(newAnswered);
    const newAnsweredUpdated = newAnsweredListCopy
      .concat(clipMain)
      .concat(clipsExtras);

    setNewAnsweredQuestions(newAnsweredUpdated);
  };

  //  ! Never used because backend will handle the new clip name.
  const setNewShorterClipName = (
    originalClipName: string,
    actualClipName: string,
  ) => {
    const charactersExceededCount = actualClipName.length - questionsMaxLength;

    const newQuestionName = originalClipName.slice(
      0,
      -charactersExceededCount - 3,
    );

    return `${newQuestionName}..? (${videoPartNumber.current})`;
  };

  const setCustomAsBackUpQuestion = () => {
    backupQuestionSelected.current = {
      index: 0,
      backUpQuestion: pendingQuestionsList[0],
    };
  };

  const stopRecordingExecuted = async (manualStop?: boolean) => {
    let clipName;
    const isRecordingForAlbum =
      questionSelected.id != null && questionSelected.id !== -1;
    const target = isRecordingForAlbum
      ? topicTargets.current.get(questionSelected.id)
      : undefined;
    const albumId = target?.albumId;

    if (noClipMainInPool()) {
      clipName = questionSelected.name;

      if (isRecordingForAlbum) {
        setBackUpQuestionSelected();
      } else {
        setCustomAsBackUpQuestion();
      }
      // ! Else block never executed because extra clips will be handle by backend.
    } else {
      clipName = `${questionSelected.name} (${videoPartNumber.current})`;

      if (clipName.length > questionsMaxLength) {
        clipName = setNewShorterClipName(questionSelected.name, clipName);
      }

      videoPartNumber.current += 1;
    }

    // A custom recording goes into the interview's album, if it has one.
    let albumIdToUse = albumId ?? scopedAlbumId;
    if (albumIdToUse == null) {
      const newAlbum = await VaultsController.createAlbum(vault.id, clipName);
      albumIdToUse = newAlbum.id;
    }
    const clipRecordedResponse = await stopRecording(
      clipName,
      albumIdToUse,
      target?.clipId,
    );
    // Answered: another take of this topic is a new recording in its album.
    if (target?.clipId != null) {
      topicTargets.current.set(questionSelected.id, {
        albumId: target.albumId,
      });
    }

    setAllClipsCount((prev) => prev + 1);

    if (noClipMainInPool()) {
      insertClipRecordedInPoolClipMain(clipRecordedResponse.clip);
    } else {
      insertClipRecordedInPoolClipsExtras(clipRecordedResponse.clip);
    }

    if (manualStop) {
      const { clipMain, clipsExtras } = poolContainer.current;

      addAllPoolClipInNewAnsweredList(
        newAnsweredQuestions,
        clipMain!,
        clipsExtras,
      );

      setShowClipRecordedWindow(true);
      setDisplayClipRecordedWindow(true);

      if (questionSelected.id !== -1) {
        removeQuestionFromPendingListAndSelectNext(questionSelected);
      }
    }
  };

  const confirmClipsRecordedAndCleanPool = () => {
    setDisplayClipRecordedWindow(false);
    setShowClipRecordedWindow(false);
    cleanPoolUpdateVideoPartNumber();
  };

  const handleRecordAction = async (
    isRecording: boolean,
    manualStop?: boolean,
  ) => {
    interviewerIsRecording.current = isRecording;
    if (isRecording) {
      if (online) {
        sendRecordingStarted();
      }
      await startRecording();
      /* Owner can start a new recording even if the clip's recorded window is been showed. If that's the case
      all clips are confirmed, and the clip's pool must be cleaned. */
      if (showClipRecordedWindow) {
        confirmClipsRecordedAndCleanPool();
      }
    } else {
      await sleep(1000);
      setForceStopRecording(true);
      await stopRecordingExecuted(manualStop);
      setForceStopRecording(false);

      if (online) {
        sendRecordingEnded();
      }
    }
  };

  useEffect(() => {
    const checkVideocallTime = async () => {
      if (sCallTimeLeft <= 0) {
        if (interviewerIsRecording.current) {
          stoppingRecording.current = true;
          await handleRecordAction(false);
          stoppingRecording.current = false;
          leaveChannel(true);
        } else if (!stoppingRecording.current) {
          leaveChannel(true);
        }
      }
      if (sCallTimeLeft < sAnnualWarningTimeVideoCall) {
        setShowWarningReachingTimeLimit(true);
      } else {
        setShowWarningReachingTimeLimit(false);
      }
    };
    checkVideocallTime();
  }, [sCallTimeLeft]);

  const showClipRecordedWindowComponent = () => (
    <ClipRecordedWindow
      className={classnames(
        styles.clipWindow,
        displayClipRecordedWindow
          ? styles.openAnimation
          : styles.closeAnimation,
      )}
      notifyDeleteClip={() => handlePossibleDeleteClip()}
      notifyClose={() => hideFast()}
    />
  );

  const showDeleteConfirmationWindowComponent = () => (
    <DeleteConfirmationWindow
      className={styles.clipWindow}
      notifyConfirmDeleteClip={() => handleDeleteAllRecordedClips()}
      notifyClose={() => setShowDeleteConfirmationWindow(false)}
    />
  );

  const showEndCallWarningComponent = () => (
    <div className={styles.endCallWarning}>
      <div className={classnames('text__body__regular__medium__white')}>
        Are you sure you want to end this call?
      </div>
      <div className={styles.endCallButtons}>
        <Button
          buttonStyle={ButtonStyle.WhiteGhost}
          onClick={() => {
            setEndCallWarning(false);
          }}
        >
          Cancel
        </Button>
        <Button
          buttonStyle={ButtonStyle.RedFilled}
          onClick={() => {
            leaveChannel();
          }}
        >
          End call
        </Button>
      </div>
    </div>
  );

  const endCallButton = () => (
    <div className={styles.endCallButton}>
      <Button
        buttonSize={ButtonSize.Large}
        buttonStyle={ButtonStyle.RedFilled}
        onClick={() => {
          setEndCallWarning(true);
        }}
        className={styles.button}
      >
        End call
      </Button>
    </div>
  );

  const updateDataAfterUpgradingVault = async (
    newTier: SubscriptionSchemeId,
  ) => {
    const updatedVault = { ...vault!, subscriptionType: newTier };

    const permissionsObject = getPermissionsByVault(subSchemes, updatedVault);

    calculateCallTimeLeft(updatedVault.videoCallProps!);

    if (permissionsObject) {
      setVaultPermissions(permissionsObject);
    }
    notifyVaultChange(updatedVault);
  };

  const handleModifyVaultSubscription = async (
    newTier: SubscriptionSchemeId,
  ) => {
    try {
      await VaultsController.modifyVaultSubscription(vault.id, newTier);
      return vault!;
    } catch (err: any) {
      return null;
    }
  };

  const handleSuccessPayment = async (newTier: SubscriptionSchemeId) => {
    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title: 'Upgrade Vault',
        message: 'Upgrade vault successful.',
        type: NotificationType.Success,
      }),
    });

    setStartPaymentProcess(false);
    updateDataAfterUpgradingVault(newTier);
  };

  const handleError = () => {
    setStartPaymentProcess(false);

    dispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title: 'Modify Vault',
        message: 'Subscription failed.',
        type: NotificationType.Error,
      }),
    });
  };

  const showAllPaymentProcessComponent = () => (
    <AllPaymentProcess
      isInsideVideoCall
      vaultReceived={vault}
      paymentType={PaymentType.ModifyVaultSubscription}
      successPaymentTextBody="Your subscription has been successfully modified."
      failedPaymentTextBody="Your subscription could not be modified. Please review your payment method and try again."
      notifyProcessConfirmation={(newTier?: SubscriptionSchemeId) =>
        handleModifyVaultSubscription(newTier!)
      }
      cancelPaymentProcess={() => setStartPaymentProcess(false)}
      successPaymentProcess={(newTier) => handleSuccessPayment(newTier)}
      notifyError={handleError}
      failedPaymentProcess={() => {
        setStartPaymentProcess(false);
      }}
    />
  );

  const showUpgradeButton = () => (
    <Button
      buttonSize={ButtonSize.Large}
      buttonStyle={ButtonStyle.PrimaryStroke}
      onClick={() => setStartPaymentProcess(true)}
      className={styles.button}
    >
      Upgrade subscription
    </Button>
  );

  const answeredRecordedList: ClipType[] = (vault?.albums ?? []).map(
    albumToClipType,
  );

  const showOwnerMenu = () => (
    <div className={styles.ownerMenu}>
      <OwnerMenu
        unanswered={pendingQuestionsList}
        answered={answeredRecordedList}
        newAnsweredQuestions={newAnsweredQuestions}
        questionSelected={questionSelected}
        clipsCountRecorded={clipsCountRecorded}
        maxNumberRecordingChunks={maxNumberRecordingChunks.current}
        clipLimitReached={allClipsCount === vault.maxClips}
        vaultQuestionsPermissionLimit={vault.maxClips}
        forceStopRecording={forceStopRecording}
        noAbleToStartRecording={
          showDeleteConfirmationWindow || allClipsCount === vault.maxClips
        }
        otherUserInCall={otherUserInCall}
        otherUserTracks={otherUserTracks}
        otherUserTrackState={otherUserTrackState}
        notifyQuestionsSelected={(question: ClipType) =>
          handleQuestionsSelected(question)
        }
        notifyRecordAction={handleRecordAction}
        notifyErrorRecording={(message: string) =>
          handleErrorRecording(message)
        }
        notifyIncrementClipCountRecorded={() => {
          setClipsCountRecorded((prevState) => prevState + 1);
        }}
        notifyResetClipCountRecorded={() => {
          setClipsCountRecorded(1);
        }}
        otherUserAudioFailed={otherUserAudioFailed}
      />
    </div>
  );

  const showCurrentQuestion = () => (
    <div className={styles.currentQuestion}>
      <div className="text__body__regular__large__gray500">
        Current question:
      </div>
      <div className="text__title1__white">{`"${questionRecording}"`}</div>
    </div>
  );

  const getButtonText = () => {
    if (showWarningReachingClipLimit && showWarningReachingTimeLimit) {
      return 'Get more time and clips';
    }
    if (showWarningReachingClipLimit) {
      return 'Get more clips';
    }
    return 'Get more time';
  };

  const showButtonToBuyTimeOrClips = () => (
    <div className={classnames(styles.upgradeButton)}>
      <Button
        buttonSize={ButtonSize.Large}
        buttonStyle={ButtonStyle.PrimaryStroke}
        onClick={() => setShowModalBuyClipsOrTime(true)}
        className={styles.button}
      >
        {getButtonText()}
      </Button>
    </div>
  );

  const showErrorNotification = () => (
    <Notification
      message={errorMessage}
      handleClose={() => setShowError(false)}
    />
  );

  const showMaxClipsAllowReachedComponent = () => (
    <div className={styles.topWarning}>
      <WarningReachingClipsLimit
        maxQuestionsPerVault={vault.maxClips}
        allClipsCount={allClipsCount}
      />
    </div>
  );

  const showTimeLimitComponent = () => (
    <div className={styles.topWarning}>
      <WarningReachingTimeLimit
        stimeLeft={sCallTimeLeft > 0 ? sCallTimeLeft : 0}
      />
    </div>
  );

  const showUserLeftCallWarningComponent = (roleLeft: VideoCallRole) => (
    <WarningUserLeftCall
      notifyClose={() => setUserLeftCall(undefined)}
      role={roleLeft}
    />
  );

  const showModalClipsTime = () => (
    <ModalBuyTimeSubscription
      onClose={() => setShowModalBuyClipsOrTime(false)}
      onBuyTime={() => {
        setShowModalBuyClipsOrTime(false);
        setShowModalBuyExtraTime(true);
      }}
      onUpgradeSubscription={() => {
        setShowModalBuyClipsOrTime(false);
        setStartPaymentProcess(true);
      }}
      onBuyClips={() => {
        setShowModalBuyClipsOrTime(false);
        setShowModalBuyClips(true);
      }}
      vault={vault}
    />
  );
  const modalBuyClips = () => (
    <BuyClipsProcess
      vault={vault}
      onFinishProcess={() => setShowModalBuyClips(false)}
    />
  );

  const modalBuyExtraTime = () => (
    <BuyExtraCallTimeProcess
      onFinishProcess={() => setShowModalBuyClips(false)}
      vault={vault}
      refetchVault={refetchVault}
    />
  );

  const modalConnectionFailed = () => (
    <ModalConnectionFailed
      handleLeaveCall={notifyEndCall}
      handleRefreshPage={refreshPage}
    />
  );

  const handleSwitchRole = (newRole: VideoCallRole) => {
    if (newRole !== role.current) {
      role.current = newRole;
      waitForRequest.current = true;
      setChangeRoleLoading(true);
      updateRole(newRole);
      if (newRole === VideoCallRole.INTERVIEWER) {
        refetchVault();
      }
      setTimeout(() => {
        setChangeRoleLoading(false);
      }, 2000);
    }
  };

  const showSwitchRoleButton = (currentRole: VideoCallRole) => {
    const selectDisabled =
      !user.permissions?.permissions.changeVideocallRole ||
      !!callInformation?.videocallRespondentIsGuest;
    return (
      <>
        <span className="text__body__regular__medium__white">Role:</span>
        <Select
          options={Object.values(VideoCallRole).map((value: string) => ({
            id: capitalizeFirstLetter(value),
            value: capitalizeFirstLetter(value),
          }))}
          optionSelected={{ id: currentRole, value: currentRole }}
          onClickOption={(option) => {
            handleSwitchRole(
              (option.id as string).toLowerCase() as VideoCallRole,
            );
          }}
          id="role"
          value={capitalizeFirstLetter(currentRole)}
          className={classnames(styles.selectRole, {
            [styles.selectDisabled]: selectDisabled,
          })}
          disabled={selectDisabled}
          tooltip={!user.permissions?.permissions.changeVideocallRole}
          tooltipContent="Available after sign up"
          tooltipId="switch-role-tooltip"
          tooltipPosition="bottom"
        />
      </>
    );
  };

  const showChangingRoleModal = () => <ModalRoleChanging />;

  // The role as the call reports it. `role.current` is only set after the
  // render that brought the call information, so on its own it can leave the
  // role control out until something else re-renders.
  const reportedRole =
    callInformation && userHasRole(callInformation, userIdForVideocall)
      ? getUserRole(callInformation, user.id)
      : role.current;

  const showVideocallWarnings = () => (
    <div className={styles.videocallWarnings}>
      {showWarningReachingClipLimit && showMaxClipsAllowReachedComponent()}
      {showWarningReachingTimeLimit && showTimeLimitComponent()}
    </div>
  );

  const showModalSignUp = () => (
    <ModalWarning
      title="Sign up to save your recordings"
      content={
        <div className={styles.modalSignUpInfo}>
          <div className="text__body__regular__medium__gray800">
            You&apos;ve run out of free trial clips! Please complete the sign up
            process to be able to save your clips and access them later,
            download or share them, as well as access the rest of our features.
          </div>
          <div className="text__body__regular__medium__gray800">
            Don&apos;t worry, your first vault is{' '}
            <span className="text__body__semi__bold__medium__gray800">
              free
            </span>{' '}
            for a month, and signing up only takes 2 minutes!
          </div>
        </div>
      }
      successButtonText="Sign up"
      closeFn={() => setShowModalSignUp(false)}
      successFn={() => goToPage(RouteName.SignUp)}
      successStyleButton={ButtonStyle.PrimaryFilled}
    />
  );

  const modalOtherUSerAudioFailed = () => (
    <ModalOtherUserAudioFailed
      notifyOnClose={() => setShowOtherUserAudioFailed(false)}
    />
  );

  return (
    <>
      {startPaymentProcess && showAllPaymentProcessComponent()}
      {showModalBuyClipsOrTime && showModalClipsTime()}
      {showModalBuyClips && modalBuyClips()}
      {showModalBuyTime && modalBuyExtraTime()}
      {showModalConnectionFailed && modalConnectionFailed()}
      {changeRoleLoading && showChangingRoleModal()}
      {showOtherUserAudioFailed && modalOtherUSerAudioFailed()}
      {signUpModal && showModalSignUp()}

      <div className={styles.VideoCallContainer}>
        <div className={styles.overlayContainer}>
          <div className={styles.topSection}>
            <div className={styles.topLeftSection}>
              <div className={styles.basicCallButtons}>
                {endCallButton()}
                {/* TODO: For now, we use <CopyLink /> component. Previously we were using <SendLinkToContributor /> */}
                {published && !otherUserInCall && (
                  <CopyLink
                    buttonSize={ButtonSize.Large}
                    buttonStyle={ButtonStyle.PrimaryStroke}
                    className={classnames(styles.linkOption, styles.button)}
                    path={`${apiBaseURL}/vaults/${vault.id}/join/${vault
                      .videoCallProps?.videoCallToken!}`}
                  >
                    <LinkIcon />
                    <div className={styles.linkText}> Copy invite link </div>
                  </CopyLink>
                )}
                <div className={styles.toggleButtons}>
                  {published && (
                    <SensorToggleButton
                      sensorType="microphone"
                      buttonSize={ButtonSize.Large}
                      disabled={recordingInProgress && !isOwner(user, vault!)}
                      iconClass={styles.iconToggle}
                    />
                  )}
                  {published && (
                    <SensorToggleButton
                      sensorType="camera"
                      buttonSize={ButtonSize.Large}
                      disabled={recordingInProgress && !isOwner(user, vault!)}
                      iconClass={styles.iconToggle}
                    />
                  )}
                </div>
                {published &&
                  !recordingInProgress &&
                  isFreeTrial(vault) &&
                  isOwner(user, vault!) &&
                  canUpgradeVault(vault, subSchemes) &&
                  userPermissionUpgrade(user) &&
                  showUpgradeButton()}
                {published &&
                  role.current === VideoCallRole.INTERVIEWER &&
                  showVideocallWarnings()}
                {role.current === VideoCallRole.INTERVIEWER &&
                  !recordingInProgress &&
                  !isFreeTrial(vault) &&
                  (showWarningReachingClipLimit ||
                    showWarningReachingTimeLimit) &&
                  showButtonToBuyTimeOrClips()}
              </div>
              {endCallWarning && showEndCallWarningComponent()}
            </div>
            {role.current === VideoCallRole.RESPONDENT &&
              recordingInProgress && (
                <div className={styles.recordingInProgress}>
                  <VideoIcon className={styles.infoIcon} />
                  <p className="text__body__regular__small__white">
                    Recording in progress...
                  </p>
                </div>
              )}
            <div className={styles.topRightSection}>
              {callInformation &&
                reportedRole &&
                showSwitchRoleButton(reportedRole)}
            </div>
          </div>
          {showError && showErrorNotification()}

          <div className={styles.bottomSection}>
            {published &&
              userLeftCall &&
              callInformation &&
              showUserLeftCallWarningComponent(userLeftCall)}
            <div className={styles.clipWindows}>
              {showDeleteConfirmationWindow &&
                showDeleteConfirmationWindowComponent()}
              {showClipRecordedWindow && showClipRecordedWindowComponent()}
            </div>
            {showRoleSwitched && callInformation && (
              <WarningRoleUpdated
                newRole={getUserRole(callInformation, user.id)}
                notifyClose={() => setShowRoleSwitched(false)}
              />
            )}
            {vault &&
              callInformation &&
              getUserRole(callInformation, user.id) ===
                VideoCallRole.INTERVIEWER &&
              showOwnerMenu()}
            {vault &&
              questionRecording &&
              role.current === VideoCallRole.RESPONDENT &&
              showCurrentQuestion()}
          </div>
        </div>

        <div className={styles.video}>
          {published && (
            <Video
              user={user}
              vault={vault!}
              myTracks={myTracks}
              isRecording={interviewerIsRecording.current}
              otherUserTracks={otherUserTracks}
              otherUserTrackState={otherUserTrackState}
              otherUserInCall={otherUserInCall}
              userRole={role.current}
              otherUserName={
                role.current === VideoCallRole.INTERVIEWER
                  ? callInformation?.videocallRespondentName
                  : callInformation?.videocallInterviewerName
              }
              otherUserIsGuest={
                role.current === VideoCallRole.INTERVIEWER &&
                !!callInformation?.videocallRespondentIsGuest
              }
            />
          )}
        </div>
      </div>
    </>
  );
};

export { VideoCall };
